import React, { useRef } from 'react';
import { FaExclamationCircle, FaEye, FaEyeSlash, FaTimes } from 'react-icons/fa';
import '../styles/CustomFormGroup.css';

const CustomFormGroup = ({
  type = 'text',
  name,
  value,
  onChange,
  onBlur,
  placeholder,
  icon,
  error,
  togglePassword = false,
  showPassword = false,
  onTogglePassword,
  options = [],
  isTwoColumn = false,
  accept,
  rows = 3,
  col = 1,
  label,
  required,
  disabled,
  checked,
  filePreview,
  readOnly = false,
  onOpenPopup,
}) => {
  const inputType = togglePassword ? (showPassword ? 'text' : 'password') : type;
  const dateRef = useRef(null);
  const handleDateInput = (e, name, onChange) => {
    const input = e.target.value;
    let raw = input.replace(/\D/g, '');

    if (raw.length > 8) raw = raw.slice(0, 8);

    let d = raw.slice(0, 2);
    let m = raw.slice(2, 4);
    let y = raw.slice(4, 8);

    // 🔒 VALIDASI DD (01–31) setelah 2 digit
    if (d.length === 2) {
      const day = Number(d);
      if (day < 1 || day > 31) return;
    }

    // 🔒 VALIDASI MM (01–12) setelah 2 digit
    if (m.length === 2) {
      const month = Number(m);
      if (month < 1 || month > 12) return;
    }

    let result = '';

    // DD
    if (raw.length <= 2) {
      result = d;
      if (raw.length === 2 && !input.endsWith('/')) {
        result += '/'; // auto slash aman
      }
    }
    // DD/MM
    else if (raw.length <= 4) {
      result = d + '/' + m;
      if (raw.length === 4 && !input.endsWith('/')) {
        result += '/';
      }
    }
    // DD/MM/YYYY
    else {
      result = d + '/' + m + '/' + y;
    }

    onChange({
      target: {
        name,
        value: result,
      },
    });
  };

  const syncTextToDatePicker = (value, dateRef) => {
    if (!value || !dateRef.current) return;

    const [d, m, y] = value.split('/');
    if (d?.length === 2 && m?.length === 2 && y?.length === 4) {
      dateRef.current.value = `${y}-${m}-${d}`;
    }
  };

  const renderInput = () => {
    if (type === 'textarea') {
      return (
        <div className={`input-wrapper col-${col}`}>
          {icon && <span className="input-icon">{icon}</span>}
          <textarea
            name={name}
            value={value}
            onChange={onChange}
            onBlur={onBlur}
            rows={rows}
            placeholder=" "
          />
          <label className={value ? 'filled' : ''}>{placeholder}</label>
        </div>
      );
    }

    if (type === 'radio') {
      return (
        <div className="radio-group">
          {/* <label className="floating-label">{label}{required && "*"}</label> */}
          <div className="radio-options">
            {options.map((option) => {
              return (
                <label key={option.value} className="radio-option">
                  <input
                    type="radio"
                    name={name}
                    value={option.value}
                    checked={value === option.value}
                    onChange={onChange}
                  />
                  {option.icon && <span className="radio-icon">{option.icon}</span>}
                  <span>{option.label}</span>
                </label>
              );
            })}
          </div>
        </div>
      );
    }

    if (type === 'file') {
      return (
        <div>
          <div className={`input-wrapper col-${col}`}>
            {icon && <span className="input-icon">{icon}</span>}
            <input
              type="file"
              name={name}
              onChange={onChange}
              accept={accept}
            />
            <label>{label}</label>
          </div>
          <div>
            {filePreview && (
              <img
                src={filePreview}
                alt={name}
                style={{ width: 100, objectFit: 'cover', marginTop: '10px' }}
              />
            )}
          </div>
        </div>
      );
    }

    if(type === 'select'){
      return (
        <div className={`input-wrapper col-${col}`}>
          {icon && <span className="input-icon">{icon}</span>}
          <select
            name={name}
            value={value}
            onChange={onChange}
            className="input-field"
            required={required}
            disabled={disabled}
          >
            <option value="" disabled>
              {placeholder}
            </option>
            {options &&
              options.map((option, idx) => (
                <option key={idx} value={option.value}>
                  {option.label}
                </option>
              ))}
          </select>
          <label>{label}</label>
        </div>
      );
    }

    if(type === 'checkbox'){
      return (
        <div className="checkbox-wrapper">
          <label className="checkbox-item">
          <input
            type="checkbox"
            checked={checked}
            onChange={onChange}
          />
          {label}
          </label>
        </div>
      );
    }

    if (type === 'date') {
      const handleIconClick = (e) => {
        e.preventDefault(); // WAJIB
        syncTextToDatePicker(value, dateRef);
        dateRef.current?.showPicker?.();
      };

      return (
        <div className={`input-wrapper col-${col}`}>
          {/* ICON CALENDAR */}
          {icon && (
            <span
              className="input-icon"
              onMouseDown={handleIconClick}
              style={{ cursor: 'pointer' }}
            >
              {icon}
            </span>
          )}

          {/* INPUT MANUAL */}
          <input
            type="text"
            name={name}
            value={value || ''}
            placeholder={placeholder || 'dd/mm/yyyy'}
            inputMode="numeric"
            maxLength={10}

            onChange={(e) => {
              handleDateInput(e, name, (event) => {
                onChange(event);
                syncTextToDatePicker(event.target.value, dateRef);
              });
            }}

            onBlur={() => {
              if (!value) return;

              const [d, m, y] = value.split('/');
              if (d && m && y?.length === 4) {
                const formatted = `${d.padStart(2, '0')}/${m.padStart(2, '0')}/${y}`;
                onChange({ target: { name, value: formatted } });
                syncTextToDatePicker(formatted, dateRef);
              }
            }}
          />

          {/* NATIVE DATE PICKER (HIDDEN) */}
          <input
            type="date"
            ref={dateRef}
            tabIndex={-1}
            style={{
              position: 'absolute',
              opacity: 0,
              pointerEvents: 'none',
              width: 0,
              height: 0,
            }}
            onChange={(e) => {
              if (!e.target.value) return;
              const [y, m, d] = e.target.value.split('-');
              onChange({
                target: { name, value: `${d}/${m}/${y}` },
              });
            }}
          />

          <label className={value ? 'filled' : ''}>{label}</label>
        </div>
      );
    }




    if (type === 'chipSelect') {
      return (
        <div className={`input-wrapper col-${col}`} onClick={() => !disabled && typeof onOpenPopup === 'function' && onOpenPopup()}>
          {icon && <span className="input-icon">{icon}</span>}
          <div
            className='chip-select-wrapper'
            style={{ flex: 1, display: 'flex', flexWrap: 'wrap', gap: '8px' }}
          >
            {value && value.length > 0 ? (
              value.map((item, idx) => (
                <span
                  key={idx}
                  style={{
                    background: '#e0e7ff',
                    color: '#4338ca',
                    borderRadius: '16px',
                    padding: '4px 10px',
                    fontSize: '0.85rem',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                  }}
                >
                  <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                    {item.name || item}
                  </span>
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      onChange({
                        target: {
                          name,
                          value: value.filter((_, i) => i !== idx),
                        },
                      });
                    }}
                    style={{
                      background: 'none',
                      border: 'none',
                      color: '#4338ca',
                      cursor: 'pointer',
                      fontSize: '16px',
                      padding: 0,
                      marginTop: '5px'
                    }}
                  >
                    <FaTimes/>
                  </button>
                </span>
              ))
            ) : (
              <span className='chip-placeholder'>{placeholder}</span>
            )}
          </div>
        </div>
      );
    }
    
    return (
      <div className={`input-wrapper col-${col}`}>
        {icon && <span className="input-icon">{icon}</span>}
        <input
          type={inputType}
          name={name}
          value={value}
          onChange={onChange}
          onBlur={onBlur}
          placeholder=" "
          autoComplete="off"
          readOnly={readOnly}
        />
        <label className={value ? 'filled' : ''}>{placeholder}</label>
        {togglePassword && (
          <span className="toggle-password" onClick={onTogglePassword}>
            {showPassword ? <FaEye /> : <FaEyeSlash />}
          </span>
        )}
      </div>
    );
  };

  return (
    <div className={`${type === 'checkbox' ? 'custom-form-group-checkbox' : 'custom-form-group'} ${isTwoColumn ? 'two-column' : ''} ${error ? 'has-error' : ''}`}>
      {renderInput()}
      {error && (
        <div className="error-text">
          <FaExclamationCircle /> {error}
        </div>
      )}
    </div>
  );
};

export default CustomFormGroup;

